<template>
  <div class="container-dot-plot-chart">
    <div class="container-chart" ref="chart"></div>
    <div v-if="noData" ref="noData">
      <p style="text-align: center">NO DATA</p>
    </div>
    <!-- <div class="container-x-axis" ref="xAxisContainer"></div> -->
  </div>
</template>

<script>
import * as d3 from 'd3';
import { tip as d3tip } from 'd3-v6-tip';
import { Container, getTooltipTemplate, ResizeObserver, XAxis, YAxis } from '../chart-services';

export default {
  name: 'DotPlot',
  props: {
    width: {
      type: String,
      default: '1080'
    },
    height: {
      type: String,
      default: '400'
    },
    autoWidth: { type: Boolean, default: true },
    margin: {
      type: String,
      default: '{"top":20,"right":100,"bottom":50,"left":320}'
    },
    isXAxisFixed: {
      type: Boolean,
      default: false
    },
    hasYAxisGrid: {
      type: Boolean,
      default: false
    },
    barThickness: {
      type: String,
      default: '25'
    },
    data: {
      type: Object, default: null
    }
  },
  data: function () {
    return {
      $chartContainer: null,
      $xAxisContainer: null,
      $xAxisWrapper: null,
      chartMargin: {
        top: 20,
        right: 100,
        bottom: 50,
        left: 420
      },
      colors: {},
      vis: [],
      width_: null,
      height_: null,
      label_font_style: 'Roboto',
      label_font_size: 20,
      noData: false,
      sort_order: 'DESC',
      x_unit_label: '',
      max_y_label_length: 0,
      tooltips: [],
      total_circles: 0,
      resizeObserver$: null,
      hasMeanLabeled: false
    };
  },
  watch: {
    selectedNode: function (newData, oldData) {
      console.log('The selected node changed...', newData, oldData);
    },
    filters: function () {
      this.initialize();
    },
    data: {
      handler: function () {
        this.initialize();
      },
      deep: true
    },
    margin: function () {
      this.initialize();
    }
  },
  mounted: function () {
    if (!this.data) {
      this.setNoDataText();
      return;
    }
    this.initialize();
  },
  destroyed: function () {
    if (this.resizeObserver$ !== null) ResizeObserver.unobserve(this.resizeObserver$);
  },
  methods: {
    setNoDataText: function () {
      this.noData = true;
      this.$refs.chart.style.display = 'none';
    },
    hideNoDataText: function () {
      this.noData = false;
      this.$refs.chart.style.display = 'block';
    },
    setChartSize: function (data) {
      const dataLength = data.data.length;
      const thickNess = Number(this.barThickness);
      const minHeight = 100;
      const heightByData = (dataLength * thickNess) + (dataLength * 11);
      const maxYLabelLength = d3.max(data.data, function (d) { return d.label.length; });

      this.max_y_label_length = maxYLabelLength;
      this.width_ = (this.autoWidth ? this.$el.offsetWidth : Number(this.width)) - this.chartMargin.left - this.chartMargin.right - this.max_y_label_length;
      this.height_ = heightByData - this.chartMargin.top - this.chartMargin.bottom;
      //   this.height_ = this.height - this.chartMargin.top - this.chartMargin.bottom;
      this.height_ = this.height_ < minHeight ? minHeight : this.height_;

      // Set sortOrder
      this.sort_order = data.sort_order ? data.sort_order : this.sort_order;
      // Set Unit label
      this.x_unit_label = data.x_unit_label ? data.x_unit_label : this.x_unit_label;

      Container.removeContainer(this.$refs.chart, { isXAxisFixed: this.isXAxisFixed });
      const containerInfo = Container.renderContainer(this.$refs.chart, this.width_, this.height + 20, this.height_ + 20, this.chartMargin, {
        isXAxisFixed: this.isXAxisFixed,
        hasXAxisLabel: data.x_label !== undefined
      });
      this.$chartContainer = containerInfo.$chartContainer;
      this.$xAxisContainer = containerInfo.$xAxisContainer;
      this.$xAxisWrapper = containerInfo.$xAxisWrapper;
      this.vis[0] = containerInfo.$chartWrapper;
    },
    fetchData: function () {
      const self = this;
      self.hideNoDataText();
      // console.log(this.data);
      if (this.data && this.data.data && this.data.data.length > 0) {
        self.setChartSize(this.data);
        self.colors = this.data.colors;
        self.tooltips = this.data.tooltip;
        self.total_circles = this.data.total_circles;
        self.renderChart(
          this.data.data,
          this.data.x_label,
          this.data.y_label,
          this.data.average_percentage
        );
        //   self.vis[0].call(self.tip);
        return;
      }
      self.setNoDataText();
    },
    renderChart: function (data, xLabel, yLabel) { // , avg = null) {
      const self = this;
      const tip = d3tip().attr('class', 'bpD3Tip').html((EVENT, d) => d);
      const thickNess = Number(self.barThickness);

      // set the ranges
      const y = YAxis.getYScale([self.height_, 0], data.map(d => d.label));
      // const x = d3.scaleLinear().range([0, self.width_]);
      const x = XAxis.getXScale([0, self.width_], [0, 100]);

      const nodeEnter = self.vis[0].selectAll('.box').data(data).enter();
      // console.log(data);
      // console.log([data[0]]);
      // const node0Enter = self.vis[0].selectAll('.box').data([data[0]]).enter();
      const boxHeight = 4;
      const totalBoxMargin = 12;
      const halfBoxMargin = totalBoxMargin / 2 + 3;
      const faColor = self.colors.fa_color;
      const fbColor = self.colors.fb_color;
      const meanColor = self.colors.mean_color;
      const avgColor = self.colors.average;

      // X Axis
      const xAxis = XAxis.getXAxis(x);
      const renderXAxisOptions = {
        translate: { x: 0, y: self.height_ }
      };
      if (xLabel !== undefined) {
        renderXAxisOptions.label = {
          fontStyle: this.label_font_style,
          fontSize: this.label_font_size,
          text: xLabel
        };
      }
      XAxis.renderXAxis(this.vis[0], xAxis, renderXAxisOptions);
      if (this.isXAxisFixed === true) {
        const renderXAxisFixedOptions = {};
        if (xLabel !== undefined) {
          renderXAxisFixedOptions.label = {
            fontStyle: this.label_font_style,
            fontSize: this.label_font_size,
            text: xLabel
          };
        }
        XAxis.renderXAxis(this.$xAxisWrapper, xAxis, renderXAxisFixedOptions);
      }
      // END: X Axis

      // Y Axis
      const yAxis = YAxis.getYAxis(y, {
        tickSizeInner: -this.width_
      });
      const renderYAxisOptions = {
        ellipsis: {
          width: this.chartMargin.left
        }
      };
      if (yLabel !== undefined) {
        renderYAxisOptions.label = {
          fontStyle: this.label_font_style,
          fontSize: this.label_font_size,
          text: yLabel,
          position: { x: -this.chartMargin.left }
        };
      }
      YAxis.renderYAxis(self.vis[0], yAxis, renderYAxisOptions);
      // this.$chartContainer.selectAll('.y-axis .tick line').each(function () {
      //   d3.select(this).attr('stroke', '#c4c4c4').attr('stroke-dasharray', '3,3');
      // });
      if (this.hasYAxisGrid === true) {
        YAxis.renderYGrid(self.vis[0], y, { position: { x: -this.chartMargin.left } });
      }
      // END:Y Axis

      // Tooltip template
      const meanTooltipTemplate = getTooltipTemplate(self.tooltips.mean, 'd', 'self');
      const faTooltipTemplate = getTooltipTemplate(self.tooltips.fa, 'd', 'self');
      const fbTooltipTemplate = getTooltipTemplate(self.tooltips.fb, 'd', 'self');

      for (let i = 0; i < self.total_circles; i++) {
        const cTooltipTemplate = self.tooltips['c' + i] ? getTooltipTemplate(self.tooltips['c' + i], 'd', 'self') : '';
        let cColor = '';
        if (i === 0) {
          cColor = fbColor;
        } else if (i === self.total_circles - 1) {
          cColor = faColor;
        } else {
          cColor = self.colors.grey_colors[i - 1];
        }
        nodeEnter
          .append('circle')
          // .attr('cx', 8)
          .attr('cy', function (d) {
            return y(d.label) + d.largest_r + 2;
          })
          .attr('cx', function (d) {
            return x(d.circles[i].d);
          })
          .attr('r', function (d) {
            return d.circles[i].r;
          })
          .attr('fill', function () {
            // const letters = '0123456789ABCDEF';
            // let color = '#';
            // for (let i = 0; i < 6; i++) {
            //   color += letters[Math.floor(Math.random() * 16)];
            // }
            return cColor;
          })
          .on('mouseover', (event, d) => {
            tip.show(event, eval('`' + cTooltipTemplate + '`'));
          })
          .on('mousemove', (event) => {
            const x = event.screenX;
            const y = event.screenY;
            tip.style('top', y + 'px');
            tip.style('left', x + 'px');
          })
          .on('mouseout', tip.hide)
          .call(tip);
      }

      nodeEnter
        .append('rect')
        .attr('width', function () {
          return boxHeight;
        })
        .attr('y', function (d) {
          return y(d.label) + halfBoxMargin;
        })
        .attr('x', function (d) {
          return x(d.mean);
        })
        .attr('height', (thickNess - totalBoxMargin) * 2)
        .attr('fill', function () {
          return meanColor;
        })
        .on('mouseover', (event, d) => {
          tip.show(event, eval('`' + meanTooltipTemplate + '`'));
        })
        .on('mousemove', (event) => {
          const x = event.screenX;
          const y = event.screenY;
          tip.style('top', y + 'px');
          tip.style('left', x + 'px');
        })
        .on('mouseout', tip.hide)
        .call(tip);

      // if (!self.hasMeanLabeled) {
      // console.log(node0Enter);
      nodeEnter
        .append('text')
        .attr('style', 'font-size:14px;')
        .attr('y', function (d) {
          return y(d.label) + halfBoxMargin - 4;
        })
        .attr('x', function (d) {
          return x(d.mean);
        })
        .attr('text-anchor', 'middle')
        .text(function (d) { return d.mean.toFixed(1) + '%'; });
      self.hasMeanLabeled = true;
      // }

      if (this.autoWidth === true && this.resizeObserver$ === null) {
        this.resizeObserver$ = ResizeObserver.observe(this.$el, () => {
          this.initialize();
        }, { onlyWidth: true, skipFirst: true });
      }
    },
    initialize: function () {
      if (this.margin) {
        Object.assign(this.chartMargin, JSON.parse(this.margin));
      }
      this.fetchData();
    }
  }
};
</script>
<style>
/* @import "../chart-services/css/tooltip.css"; */

.container-dot-plot-chart .bpD3Tip {
  font-family: "Roboto", sans-serif;
  font-weight: 700;
  background-color: white !important;
  border: 1px solid #ff6b18;
  padding: 5px 5px 5px 5px;
  color: #2e2e2e;
  font-size: 10px;
}

.container-dot-plot-chart .x-axis path {
  stroke: white;
}

.container-dot-plot-chart .x-axis .tick line {
  stroke: #434343;
  stroke-width: 2;
}

.container-dot-plot-chart .x-axis .tick line {
  stroke-dasharray: 0, 0;
}

.container-dot-plot-chart .x-axis .tick text,
.container-dot-plot-chart .x-axis-label {
  fill: var(--tbd-chart-text-color, #434343);
  font-size: 18px;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
}

.container-dot-plot-chart .y-axis path {
  stroke: white;
}
.container-dot-plot-chart .y-axis .tick line {
  stroke: #bbb;
}

.container-dot-plot-chart .y-axis .tick line {
  stroke-width: 1;
}

.container-dot-plot-chart .y-axis .tick text,
.container-dot-plot-chart .y-axis-label {
  fill: var(--tbd-chart-text-color, #434343);
  font-size: 22px;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
}

.container-dot-plot-chart .y-grid-line line {
  stroke: darkgray;
  stroke-opacity: 1;
  shape-rendering: crispEdges;
}
</style>
